import { createContext, ReactNode, useContext, useMemo } from 'react'
import { SxProps, Theme } from '@mui/material'
import { useFullscreenContainer } from '../../hooks/useFullscreenContainer'

type Horizontal = 'left' | 'center' | 'right'

/** Above chart settings dialog (99999) and MUI Dialog defaults. */
export const SETTINGS_OVERLAY_Z_INDEX = 100000

type WidgetSettingsMenuContextValue = {
    fullscreenContainer: HTMLElement | null
    inSettingsDialog: boolean
}

const WidgetSettingsMenuContext = createContext<WidgetSettingsMenuContextValue>({
    fullscreenContainer: null,
    inSettingsDialog: false,
})

export const WidgetSettingsMenuProvider = ({ children }: { children: ReactNode }) => {
    const fullscreenContainer = useFullscreenContainer()
    const value = useMemo(
        () => ({
            fullscreenContainer,
            inSettingsDialog: true,
        }),
        [fullscreenContainer]
    )

    return (
        <WidgetSettingsMenuContext.Provider value={value}>
            {children}
        </WidgetSettingsMenuContext.Provider>
    )
}

export const useWidgetSettingsMenuPlacement = (horizontal: Horizontal = 'left') => {
    const { fullscreenContainer, inSettingsDialog } = useContext(WidgetSettingsMenuContext)
    const localFullscreenContainer = useFullscreenContainer()

    return useMemo(() => {
        const container = (fullscreenContainer ?? localFullscreenContainer) ?? undefined
        const overlaySx: SxProps<Theme> = inSettingsDialog ? { zIndex: SETTINGS_OVERLAY_Z_INDEX } : {}
        const paperOverlaySx: SxProps<Theme> = inSettingsDialog ? { zIndex: SETTINGS_OVERLAY_Z_INDEX } : {}
        return {
            anchorOrigin: {
                vertical: 'bottom' as const,
                horizontal,
            },
            transformOrigin: {
                vertical: 'top' as const,
                horizontal,
            },
            container,
            // Portal out of draggable dialog paper so overlays aren't clipped or stacked behind it.
            disablePortal: false,
            overlaySx,
            paperOverlaySx,
        }
    }, [fullscreenContainer, localFullscreenContainer, inSettingsDialog, horizontal])
}

export const useWidgetSettingsSelectMenuProps = (horizontal: Horizontal = 'left') => {
    const { anchorOrigin, transformOrigin, container, disablePortal, overlaySx } =
        useWidgetSettingsMenuPlacement(horizontal)
    return useMemo(
        () => ({
            anchorOrigin,
            transformOrigin,
            container,
            disablePortal,
            sx: overlaySx,
        }),
        [anchorOrigin, transformOrigin, container, disablePortal, overlaySx]
    )
}

export const useWidgetSettingsMenuProps = (horizontal: Horizontal = 'left') => {
    const { anchorOrigin, transformOrigin, container, disablePortal, overlaySx } =
        useWidgetSettingsMenuPlacement(horizontal)
    return useMemo(
        () => ({
            anchorOrigin,
            transformOrigin,
            container,
            disablePortal,
            sx: overlaySx,
        }),
        [anchorOrigin, transformOrigin, container, disablePortal, overlaySx]
    )
}
